import type {
  CreateSubmissionRequest,
  CreateSubmissionResponse,
  Submission,
  SubmissionsCheckResponse,
} from '@fyf/shared';

// API_BASE is set by Vite's define plugin at build time
declare const __API_BASE_URL__: string | undefined;
const API_BASE = typeof __API_BASE_URL__ !== 'undefined' ? __API_BASE_URL__ : '';

export class SubmissionPausedError extends Error {
  constructor(message = 'Submissions are temporarily paused') {
    super(message);
    this.name = 'SubmissionPausedError';
  }
}

export async function createSubmission(
  data: CreateSubmissionRequest
): Promise<CreateSubmissionResponse> {
  const res = await fetch(`${API_BASE}/submissions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (res.status === 503) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new SubmissionPausedError(body.error);
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed with status ${res.status}`);
  }

  return res.json() as Promise<CreateSubmissionResponse>;
}

export async function checkSubmissions(
  surfaceTag: string,
  referralCode?: string
): Promise<SubmissionsCheckResponse> {
  let url = `${API_BASE}/submissions/check?surface=${encodeURIComponent(surfaceTag)}`;
  if (referralCode) {
    url += `&ref=${encodeURIComponent(referralCode)}`;
  }
  const res = await fetch(url);
  return res.json() as Promise<SubmissionsCheckResponse>;
}

export async function getSubmission(id: string): Promise<Submission> {
  const res = await fetch(`${API_BASE}/submissions/${encodeURIComponent(id)}`);

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed with status ${res.status}`);
  }

  return res.json() as Promise<Submission>;
}
